import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSubscriptionContext } from '../contexts/SubscriptionContext';
import { useMyOrganization } from '../hooks/useMyOrganization';
import { isSandboxOrganization } from '../utils/sandboxUtils';
import config from '../config';

const ACTIVE_COMPATIBLE_STATUSES = new Set(['Active', 'Trialing', 'PastDue', 'Canceled']);

function subscriptionAllowsAccess(subscription) {
    if (!subscription) return true;

    if (subscription.isActive === true) return true;
    if (subscription.isActive === false) return false;

    if (subscription.tier === 'Free') return true;
    return ACTIVE_COMPATIBLE_STATUSES.has(subscription.status);
}

function isRecordsOnlySubscription(subscription) {
    return subscription?.isRecordsOnly === true || subscription?.accessMode === 'RecordsOnly';
}

function isRecordsOnlyAllowedRoute(pathname) {
    return pathname === '/midwife/records-access'
        || pathname === '/midwife/clients'
        || pathname.startsWith('/charting/')
        || pathname.startsWith('/midwife/billing')
        || pathname.startsWith('/midwife/user-settings')
        || pathname === '/midwife/settings';
}

/**
 * Route guard that redirects midwives with no active subscription to billing.
 * Only active in deployed production — local, dev, and staging pass through unconditionally.
 * Billing pages are always allowed through to prevent redirect loops.
 * While the subscription is loading, navigation is unblocked to avoid flicker.
 */
export default function BillingGuard({ children }) {
    const { subscription, loading, error } = useSubscriptionContext();
    const { data: organization, isLoading: organizationLoading } = useMyOrganization();
    const { pathname } = useLocation();

    // Disabled in dev / staging — subscription is not set up in those envs
    if (!config.isSubscriptionGatingEnabled) return children;

    // Always allow billing pages through (prevents redirect loop)
    if (pathname.startsWith('/midwife/billing') || pathname.startsWith('/midwife/onboarding') || pathname === '/midwife/notifications') {
        return children;
    }

    // While billing or organization data is loading, don't block
    if (loading || organizationLoading) return children;

    // Do not lock users out on an ambiguous billing check. The API middleware
    // still enforces explicit 402 responses for genuinely inactive accounts.
    if (error) return children;

    if (isSandboxOrganization(organization)) {
        return children;
    }

    if (isRecordsOnlySubscription(subscription)) {
        if (isRecordsOnlyAllowedRoute(pathname)) {
            return children;
        }

        return <Navigate to="/midwife/records-access" replace />;
    }

    // Explicitly inactive subscription — send to billing
    if (!subscriptionAllowsAccess(subscription)) {
        return <Navigate to="/midwife/billing?welcome=1" replace />;
    }

    return children;
}
